/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControlStatus, FormGroup, PristineChangeEvent } from '@angular/forms';
import { WA_WINDOW } from '@ng-web-apis/common';
import { StandardErrorMessage } from '@rma/dashboard/generic/domain/to-be-sorted';
import { DirtyCheckConfig, dirtyFormCheck, getControlValue } from '@rma/dashboard/generic/util/dirty-form-check';
import { getServerError } from '@rma/dashboard/generic/util/validation';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import {
  BehaviorSubject,
  concatMap,
  filter,
  fromEvent,
  isObservable,
  map,
  merge,
  Observable,
  of,
  OperatorFunction,
  ReplaySubject,
  startWith,
  Subject,
  take,
  withLatestFrom,
} from 'rxjs';
import { FormState, SubmissionError, SubmissionFormOptions, SubmissionResult, SubmissionState } from '../domain/submission-form';
import { FormValueConfig, validateAllFormFields } from '../util-helpers/helpers';

export abstract class BaseSubmissionForm<T, TResult> {
  private readonly initialValueSet = new ReplaySubject<boolean>(1);
  private readonly formStatus$: Observable<FormControlStatus>;

  public readonly submitting$: Observable<boolean>;
  public readonly invalid$: Observable<boolean>;
  public readonly onSuccess$: Observable<TResult>;
  public readonly onError$: Observable<SubmissionError>;
  public readonly initialValueSet$: Observable<boolean>;
  public readonly isDirty$: Observable<boolean>;
  public readonly scrollToError$: Observable<Date>; // We are using date so that ngOnChanges recognises subsequent value changes
  public initialFormValue: T;

  private state = new BehaviorSubject<FormState>('initial');
  private triggerScrollTo = new Subject<void>();

  constructor(
    public readonly form: FormGroup,
    submitOperator: OperatorFunction<T, TResult>,
    initialValue: Partial<T> | Observable<Partial<T>> = {},
    options: Partial<SubmissionFormOptions>,
  ) {
    this.initialValueSet$ = this.initialValueSet.asObservable();
    this.submitting$ = this.state.pipe(map((status) => status === 'submitting'));

    this.formStatus$ = form.statusChanges.pipe(startWith(form.status), rmaShareReplay());
    const valid$ = this.formStatus$.pipe(map((status) => status === 'VALID'));

    this.invalid$ = this.formStatus$.pipe(map((status) => status === 'INVALID'));
    this.scrollToError$ = this.triggerScrollTo.pipe(
      withLatestFrom(this.invalid$),
      map(([_, invalid]) => invalid),
      filter((a) => a),
      map(() => new Date()),
    );

    const submissions$ = this.createFormSubmissionStream(valid$, submitOperator);

    this.onSuccess$ = submissions$.pipe(
      filter((res) => res.type === 'success'),
      // SAFETY(legacy): unreviewed, pre-dates this rule.
      map((res) => (res.type === 'success' ? res.payload : ({} as TResult))),
    );

    this.onError$ = submissions$.pipe(
      filter((res) => res.type === 'error'),
      // SAFETY(legacy): unreviewed, pre-dates this rule.
      map((res) => (res.type === 'error' ? res.error : ({} as SubmissionError))),
    );

    this.setInitialValue(initialValue, options.initialValueConfig, options.dirtyCheckConfig?.withDisabled ?? true);

    this.isDirty$ = this.getIsDirty(options.dirtyCheckConfig ?? { enabled: false });
  }

  public validate(): void {
    validateAllFormFields(this.form);
  }

  public validateAndSubmit(): void {
    this.validate();
    this.triggerScrollTo.next();

    this.formStatus$
      .pipe(
        filter((status) => status !== 'PENDING'),
        take(1),
      )
      .subscribe(() => {
        if (this.form.valid) {
          this.form.markAsPristine();
          this.setState('submitting');
        }
      });
  }

  public reset(options: FormValueConfig = { emitEvent: false }): void {
    this.form.reset({}, options);
    this.setState('initial');
  }

  public makePristine(options: FormValueConfig = { onlySelf: false }): void {
    this.form.markAsPristine(options);
    this.setState('initial');
  }

  public makeDirty(options: FormValueConfig = { onlySelf: false }): void {
    this.form.markAsDirty(options);
  }

  public abstract createFormSubmissionStream(
    valid$: Observable<boolean>,
    submitOperator: OperatorFunction<any, TResult>,
  ): Observable<SubmissionResult<TResult>>;

  protected getServerError(errorResponse: HttpErrorResponse): SubmissionError {
    const { errors } = getServerError(errorResponse);

    if (errors?.length) {
      return { errors };
    }

    return {
      errors: [
        {
          isFieldError: false,
          errorMessage: StandardErrorMessage,
          severity: 'Error',
        },
      ],
    };
  }

  protected setSubmissionState(state: SubmissionState) {
    this.setState(state);
  }

  private setState(state: FormState) {
    this.state.next(state);
  }

  private setInitialValue(
    initialValue: Partial<T> | Observable<Partial<T>>,
    options: FormValueConfig = { emitEvent: false },
    withDisabled = true,
  ) {
    if (isObservable(initialValue)) {
      initialValue.pipe(take(1)).subscribe((val) => this.patchInitialValue(val, options, withDisabled));
    } else {
      this.patchInitialValue(initialValue, options, withDisabled);
    }
  }

  private patchInitialValue(initialValue: Partial<T>, options: FormValueConfig = { emitEvent: false }, withDisabled: boolean) {
    this.form.patchValue(initialValue, options);
    this.initialFormValue = getControlValue(this.form, withDisabled);
    this.initialValueSet.next(true);
  }

  private getIsDirty(config: DirtyCheckConfig & { useBeforeunloadEvent?: boolean }): Observable<boolean> {
    if (config.enabled) {
      if (config.useBeforeunloadEvent) {
        const window = inject(WA_WINDOW);

        fromEvent(window, 'beforeunload')
          .pipe(takeUntilDestroyed())
          .subscribe((event) => {
            const isDirty = dirtyFormCheck(this.form, this.initialFormValue, config);

            if (isDirty) {
              event.preventDefault();
            }
          });
      }

      const dirtyEvent$ = this.form.events.pipe(filter((event) => event instanceof PristineChangeEvent));
      const initalValueSet$ = this.initialValueSet$.pipe(filter(Boolean));

      return merge(dirtyEvent$, initalValueSet$).pipe(concatMap(() => of(dirtyFormCheck(this.form, this.initialFormValue, config))));
    } else {
      return of(false);
    }
  }
}
